import {
  Button,
  LanguageProvider,
  LanguageSwitch,
  Logo,
  ProductTile,
  SplitCue,
  Watermark,
  useLanguageValue,
} from "@vcn/ui";
import { useContent, type PanelContent } from "./content";

function Panel({ panel }: { panel: PanelContent }) {
  return (
    <div
      data-brand={panel.brand}
      className={[
        "relative flex flex-col items-center justify-center px-6 py-6 text-center sm:py-10 lg:py-20",
        "vcn-reveal",
        "vcn-reveal-in",
      ].join(" ")}
    >
      <div className="relative mb-6 sm:mb-8 lg:mb-10">
        <Watermark brand={panel.brand} className="w-[101vw] lg:w-[40vw]" />
        {/* Kachelgröße wie im Entwurf: 121/375 mobil, 240/1920 auf Desktop. */}
        <ProductTile
          brand={panel.brand}
          width="clamp(7.5rem, 32vw, 15rem)"
          glow
          float
        />
      </div>

      <h2 className="mb-6 text-xl leading-tight font-bold text-balance text-white sm:mb-8 sm:text-2xl lg:text-[2.1rem]">
        {panel.headline.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </h2>

      {/* Buttons: mobil 338/375 breit, Desktop 357/1920. */}
      <div className="flex w-[90vw] max-w-[22rem] flex-col gap-2.5 lg:w-[18.6vw] lg:max-w-none">
        <Button href={panel.primary.href} variant="primary">
          {panel.primary.label}
        </Button>
        <Button href={panel.secondary.href} variant="secondary">
          {panel.secondary.label}
        </Button>
      </div>
    </div>
  );
}

function Page() {
  const { language, setLanguage } = useLanguageValue();
  const { panels, footerLinks, ui } = useContent();
  const [capPanel, hubPanel] = panels;

  return (
    <div className="vcn-page relative flex min-h-svh flex-col">
      <a
        href="#inhalt"
        className="sr-only rounded-full bg-white px-4 py-2 text-sm font-bold text-cap-800 focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60]"
      >
        {ui.skipToContent}
      </a>

      {/*
        Der Entwurf zeigt auf der Startseite nur das zentrierte Logo. Der
        Sprachumschalter kam später dazu: er sitzt wie auf den Landingpages
        rechts, das Logo bleibt dabei mittig stehen.
      */}
      <header className="relative z-40 flex items-center justify-center px-6 py-5 sm:px-10 sm:py-6">
        <Logo href={null} className="h-7 sm:h-9" />
        {/* Der Umschalter holt seine Farben aus den Marken-Variablen; die
            Startseite selbst setzt keine, deshalb hier die CAP-Fassung. */}
        <div data-brand="cap" className="absolute right-6 sm:right-10">
          <LanguageSwitch value={language} onChange={setLanguage} />
        </div>
      </header>

      {/*
        `main` ist selbst das Grid: als Flex-Kind mit `grow` bekommt es die
        Resthöhe, `content-center` zentriert die Zeilen darin senkrecht.
        Ein zusätzlicher Wrapper mit `h-full` würde nicht funktionieren – die
        Prozenthöhe hätte keinen Bezugswert.
      */}
      {/*
        `minmax(0,1fr)` statt `1fr`: sonst wächst die Spalte auf die
        Mindestbreite ihres Inhalts (Buttons mit max-w-[22rem] plus
        Innenabstand) und ragt auf schmalen Displays über den Rand hinaus.
      */}
      <main
        id="inhalt"
        className="relative grid grow content-center grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center"
      >
        <Panel panel={capPanel} />

        {/* Dreieck-Paar zwischen den Hälften – im Entwurf nur in der
            mobilen Fassung, wo beide Bereiche untereinander stehen. */}
        <div className="py-3 lg:hidden">
          <SplitCue href="#carrier-hub" label={ui.toCarrierHub} />
        </div>

        <div id="carrier-hub" className="scroll-mt-24">
          <Panel panel={hubPanel} />
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/15 bg-black/15 px-6 py-4 backdrop-blur-sm sm:px-10 sm:py-6">
        <nav aria-label={ui.legal}>
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/90">
            {footerLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-white"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </footer>
    </div>
  );
}

export function App() {
  return (
    <LanguageProvider>
      <Page />
    </LanguageProvider>
  );
}
